import appConfig from "@/config";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Profiel } from "@/lib/profiel_types";

interface OAuthConfig {
  baseUrl: string;
  params: Record<string, string>;
}

function getRedirectUri(provider: "google" | "microsoft" | "apple"): string {
  if (provider === "apple") return appConfig.appleRedirectURI;

  if (Capacitor.isNativePlatform()) {
    if (provider === "google") {
      const platform = Capacitor.getPlatform();
      if (platform === "android") {
        // Android uses web redirect URI - the callback page will handle
        // redirecting to the custom scheme since Google doesn't allow
        // custom schemes as redirect URIs for web clients
        return appConfig.androidGoogleRedirectURI;
      }
      return appConfig.iosGoogleRedirectURI;
    }
    return appConfig.nativeRedirectURI;
  }
  return appConfig.redirectUri;
}

/**
 * Get the appropriate client ID based on platform
 * - iOS: Uses iOS-specific client ID (reversed format)
 * - Android: Uses web client ID (Google recommends this for browser-based OAuth on Android)
 * - Web: Uses web client ID
 */
function getGoogleClientId(): string {
  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    if (platform === "android") {
      // Android uses the web client ID for browser-based OAuth
      return appConfig.googleClientId;
    }
    return appConfig.iosGoogleClientId;
  }
  return appConfig.googleClientId;
}

export function buildOAuthUrl(
  provider: "google" | "microsoft" | "apple",
  intent: string,
  role?: "parent" | "student",
  inviteToken?: string,
  profiel?: Profiel,
  checkoutFrom?: string,
  checkoutPlanId?: string
): string {
  const redirectUri = getRedirectUri(provider);

  // Include platform in state for Android Google OAuth
  // so callback page knows to redirect to custom scheme
  const platform = Capacitor.isNativePlatform()
    ? Capacitor.getPlatform()
    : "web";

  if (profiel && typeof window !== "undefined") {
    sessionStorage.setItem("oauth_profiel", JSON.stringify(profiel));
  }

  const stateParam = JSON.stringify({
    provider,
    role,
    intent,
    redirect_uri: redirectUri,
    platform,
    ...(inviteToken && { token: inviteToken }),
    ...(checkoutFrom && { checkout_from: checkoutFrom }),
    ...(checkoutPlanId && { plan_id: checkoutPlanId })
  });

  if (provider === "apple") {
    const params = new URLSearchParams({
      response_type: "code id_token",
      response_mode: "form_post",
      client_id: appConfig.appleClientId,
      redirect_uri: redirectUri,
      scope: "name email",
      state: stateParam
    });

    return `https://appleid.apple.com/auth/authorize?${params.toString()}`;
  }

  const config: OAuthConfig =
    provider === "google"
      ? {
          baseUrl: "https://accounts.google.com/o/oauth2/v2/auth",
          params: {
            response_type: "code",
            client_id: getGoogleClientId(),
            redirect_uri: redirectUri,
            scope: "email profile",
            access_type: "offline",
            prompt: "consent",
            state: stateParam
          }
        }
      : {
          baseUrl:
            "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
          params: {
            response_type: "code",
            response_mode: "query",
            client_id: appConfig.microsoftClientId,
            redirect_uri: redirectUri,
            scope: "openid email profile",
            state: stateParam
          }
        };

  const urlParams = new URLSearchParams(config.params);
  return `${config.baseUrl}?${urlParams.toString()}`;
}

/**
 * Redirect to OAuth provider.
 * * On native app: Opens in-app browser modal (ASWebAuthenticationSession on iOS)
 * On web: Regular browser redirect
 */
export async function redirectToOAuth(
  provider: "google" | "microsoft" | "apple",
  intent: string,
  role?: "parent" | "student",
  inviteToken?: string,
  profiel?: Profiel,
  checkoutFrom?: string,
  checkoutPlanId?: string
): Promise<void> {
  const url = buildOAuthUrl(
    provider,
    intent,
    role,
    inviteToken,
    profiel,
    checkoutFrom,
    checkoutPlanId
  );

  if (Capacitor.isNativePlatform()) {
    // Open in-app browser modal (uses ASWebAuthenticationSession on iOS)
    // This shows a Safari-like modal that closes automatically when
    // redirected to our custom URL scheme (wiswiz://)
    await Browser.open({
      url,
      presentationStyle: "popover" // Modal presentation on iOS
    });
  } else {
    // Regular web redirect
    window.location.href = url;
  }
}
